import { useEffect, useState } from "react"
import { ChevronLeft, ChevronRight, Edit2, Plus, Trash2 } from "react-feather"
import { OwnerMemoForm, type OwnerMemoFormData } from "@/components/properties/OwnerMemoForm"
import { Button } from "@/components/ui/button/button"
import { Card, CardContent, EmptyCard, MemoListCard } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog/dialog"

export interface OwnerMemoItem {
  id: string
  date: string
  title: string
  description: string
  images?: string[]
  /** 内部使用: 既存ファイル情報 */
  _fileInfos?: { id: number; url: string }[]
}

interface OwnerMemoSectionProps {
  title?: string
  description?: string
  memos: OwnerMemoItem[]
  onMemosChange?: (memos: OwnerMemoItem[]) => void
  onCreateMemo?: (
    data: OwnerMemoFormData
  ) => Promise<OwnerMemoItem | undefined> | OwnerMemoItem | undefined
  onUpdateMemo?: (
    memoId: string,
    data: OwnerMemoFormData
  ) => Promise<OwnerMemoItem | undefined> | OwnerMemoItem | undefined
  onDeleteMemo?: (memoId: string) => Promise<boolean | undefined> | boolean | undefined
  addButtonLabel?: string
  detailButtonLabel?: string
  loadMoreLabel?: string
  onLoadMore?: () => void
  showAddModal?: boolean
  onShowAddModalChange?: (show: boolean) => void
  hideHeader?: boolean
  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
  isLoadingMore?: boolean
}

const defaultFormState: OwnerMemoFormData = {
  title: "",
  description: "",
  images: [],
  newFiles: [],
  existingFileIds: [],
  deleteFileIds: [],
}

function formatDateTime(date: Date) {
  const year = date.getFullYear()
  const month = `${date.getMonth() + 1}`.padStart(2, "0")
  const day = `${date.getDate()}`.padStart(2, "0")
  const hours = `${date.getHours()}`.padStart(2, "0")
  const minutes = `${date.getMinutes()}`.padStart(2, "0")
  return `${year}-${month}-${day} ${hours}:${minutes}`
}

export function OwnerMemoSection({
  title = "オーナーメモ",
  description = "カスタマーセンターの対応には影響しませんので、自身のメモとしてご活用ください",
  memos,
  onMemosChange,
  onCreateMemo,
  onUpdateMemo,
  onDeleteMemo,
  addButtonLabel = "追加",
  detailButtonLabel = "詳細を見る",
  loadMoreLabel = "もっと見る",
  onLoadMore,
  showAddModal: externalShowAddModal,
  onShowAddModalChange,
  hideHeader = false,
  isCreating = false,
  isUpdating = false,
  isDeleting = false,
  isLoadingMore = false,
}: OwnerMemoSectionProps) {
  const [memoList, setMemoList] = useState<OwnerMemoItem[]>(memos)
  const [memoForm, setMemoForm] = useState<OwnerMemoFormData>(defaultFormState)
  const [selectedMemo, setSelectedMemo] = useState<OwnerMemoItem | null>(null)
  const [internalShowAddModal, setInternalShowAddModal] = useState(false)

  const isControlled = externalShowAddModal !== undefined
  const showAddModal = isControlled ? externalShowAddModal : internalShowAddModal
  const setShowAddModal = (value: boolean) => {
    if (isControlled) {
      onShowAddModalChange?.(value)
    } else {
      setInternalShowAddModal(value)
    }
  }
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  useEffect(() => {
    setMemoList(memos)
  }, [memos])

  // 追加モーダルが開かれたときにフォームをリセット（外部制御時の対応）
  useEffect(() => {
    if (showAddModal) {
      setMemoForm(defaultFormState)
    }
  }, [showAddModal])

  const handleFilesSelect = (files: FileList | File[]) => {
    if (!files) return

    const remainingSlots = 3 - memoForm.images.length
    const filesToAdd = Array.from(files).slice(0, remainingSlots)

    for (const file of filesToAdd) {
      const reader = new FileReader()
      reader.onloadend = () => {
        const dataUrl = reader.result as string
        setMemoForm((prev) => ({
          ...prev,
          images: [...prev.images, dataUrl],
          newFiles: [...(prev.newFiles ?? []), file],
        }))
      }
      reader.readAsDataURL(file)
    }
  }

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files) {
      handleFilesSelect(files)
    }

    if (event.target) {
      event.target.value = ""
    }
  }

  const handleRemoveImage = (index: number) => {
    setMemoForm((prev) => {
      const existingFileCount = prev.existingFileIds?.length ?? 0

      // 既存ファイルを削除する場合
      if (index < existingFileCount) {
        const fileIdToDelete = prev.existingFileIds?.[index]
        return {
          ...prev,
          images: prev.images.filter((_, i) => i !== index),
          existingFileIds: prev.existingFileIds?.filter((_, i) => i !== index),
          deleteFileIds: fileIdToDelete
            ? [...(prev.deleteFileIds ?? []), fileIdToDelete]
            : prev.deleteFileIds,
        }
      }

      // 新規ファイルを削除する場合
      const newFileIndex = index - existingFileCount
      return {
        ...prev,
        images: prev.images.filter((_, i) => i !== index),
        newFiles: prev.newFiles?.filter((_, i) => i !== newFileIndex),
      }
    })
  }

  const handleAddMemo = () => {
    setMemoForm(defaultFormState)
    setShowAddModal(true)
  }

  const handleEditMemo = (memo: OwnerMemoItem) => {
    setSelectedMemo(memo)
    setMemoForm({
      title: memo.title,
      description: memo.description,
      images: memo.images ?? [],
      newFiles: [],
      existingFileIds: memo._fileInfos?.map((f) => f.id) ?? [],
      deleteFileIds: [],
    })
    setShowEditModal(true)
  }

  const handleShowDetail = (memo: OwnerMemoItem) => {
    setSelectedMemo(memo)
    setCurrentImageIndex(0)
    setShowDetailModal(true)
  }

  const createNewMemo = (data: OwnerMemoFormData): OwnerMemoItem => ({
    id:
      globalThis.crypto && "randomUUID" in globalThis.crypto
        ? globalThis.crypto.randomUUID()
        : Date.now().toString(),
    date: formatDateTime(new Date()),
    title: data.title,
    description: data.description,
    images: data.images,
  })

  const updateMemoList = (updater: (current: OwnerMemoItem[]) => OwnerMemoItem[]) => {
    setMemoList((prev) => {
      const updated = updater(prev)
      onMemosChange?.(updated)
      return updated
    })
  }

  const handleSaveAdd = async () => {
    if (!memoForm.title.trim() && !memoForm.description.trim()) {
      setShowAddModal(false)
      return
    }

    try {
      const created = (await onCreateMemo?.(memoForm)) ?? createNewMemo(memoForm)
      updateMemoList((prev) => [created, ...prev])
      setShowAddModal(false)
      setMemoForm(defaultFormState)
    } catch (error) {
      // 表示は onError が担当。入力を残すため閉じない
      console.error("Failed to create memo", error)
    }
  }

  const handleSaveEdit = async () => {
    if (!selectedMemo) return

    try {
      const updatedMemo = (await onUpdateMemo?.(selectedMemo.id, memoForm)) ?? {
        ...selectedMemo,
        title: memoForm.title,
        description: memoForm.description,
        images: memoForm.images,
      }

      updateMemoList((prev) =>
        prev.map((memo) => (memo.id === updatedMemo.id ? updatedMemo : memo))
      )
      setShowEditModal(false)
      setMemoForm(defaultFormState)
      setSelectedMemo(null)
    } catch (error) {
      // 表示は onError が担当。入力を残すため閉じない
      console.error("Failed to update memo", error)
    }
  }

  const handleDeleteConfirm = async () => {
    if (!selectedMemo) return

    try {
      const shouldDelete = (await onDeleteMemo?.(selectedMemo.id)) ?? true
      if (!shouldDelete) {
        setShowDeleteDialog(false)
        return
      }

      updateMemoList((prev) => prev.filter((memo) => memo.id !== selectedMemo.id))
    } catch (error) {
      console.error("Failed to delete memo", error)
    } finally {
      setShowDeleteDialog(false)
      setShowEditModal(false)
      setShowDetailModal(false)
      setSelectedMemo(null)
    }
  }

  const nextImage = () => {
    const imageCount = selectedMemo?.images?.length ?? 0
    if (imageCount === 0) return
    setCurrentImageIndex((prev) => (prev + 1) % imageCount)
  }

  const prevImage = () => {
    const imageCount = selectedMemo?.images?.length ?? 0
    if (imageCount === 0) return
    setCurrentImageIndex((prev) => (prev - 1 + imageCount) % imageCount)
  }

  return (
    <section className="space-y-4">
      {!hideHeader && (
        <>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-[#354267]">{title}</h2>
            </div>
            <Button onClick={handleAddMemo} className="gap-1.5">
              <Plus className="h-3.5 w-3.5" />
              {addButtonLabel}
            </Button>
          </div>

          <Card className="border border-slate-200 bg-slate-100">
            <CardContent className="p-3">
              <p className="text-xs font-semibold text-[#354267]">{description}</p>
            </CardContent>
          </Card>
        </>
      )}

      <div className="space-y-3">
        {memoList.length > 0 ? (
          memoList.map((memo) => (
            <MemoListCard
              key={memo.id}
              date={memo.date}
              title={memo.title}
              description={memo.description}
              buttonText={detailButtonLabel}
              imageUrls={memo.images}
              onButtonClick={() => handleShowDetail(memo)}
              onEdit={() => handleEditMemo(memo)}
              className="p-4"
            />
          ))
        ) : (
          <EmptyCard text="メモはまだありません" />
        )}
      </div>

      {onLoadMore && (
        <Button
          variant="secondary"
          block
          onClick={onLoadMore}
          loading={isLoadingMore}
          disabled={isLoadingMore}
        >
          {loadMoreLabel}
        </Button>
      )}

      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-[90%] md:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#354267]">
              オーナーメモの追加
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <OwnerMemoForm
              formData={memoForm}
              onFormChange={setMemoForm}
              onImageUpload={handleImageUpload}
              onFilesSelect={handleFilesSelect}
              onRemoveImage={handleRemoveImage}
              inputIdPrefix="owner-memo-add"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowAddModal(false)} disabled={isCreating}>
              キャンセル
            </Button>
            <Button
              onClick={handleSaveAdd}
              className="bg-[#354267] hover:bg-[#354267]/90"
              loading={isCreating}
              disabled={isCreating}
            >
              保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-[90%] md:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#354267]">
              オーナーメモの編集
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <OwnerMemoForm
              formData={memoForm}
              onFormChange={setMemoForm}
              onImageUpload={handleImageUpload}
              onFilesSelect={handleFilesSelect}
              onRemoveImage={handleRemoveImage}
              inputIdPrefix="owner-memo-edit"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              className="mr-auto gap-2 border-red-500 text-red-500 hover:bg-red-50"
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 className="h-4 w-4" />
              削除
            </Button>
            <Button variant="outline" onClick={() => setShowEditModal(false)} disabled={isUpdating}>
              キャンセル
            </Button>
            <Button
              onClick={handleSaveEdit}
              className="bg-[#354267] hover:bg-[#354267]/90"
              loading={isUpdating}
              disabled={isUpdating}
            >
              保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-[90%] md:max-w-md">
          <DialogHeader>
            <p className="text-xs text-slate-400">{selectedMemo?.date}</p>
            <DialogTitle className="text-base font-bold text-[#354267]">
              {selectedMemo?.title}
            </DialogTitle>
          </DialogHeader>
          {selectedMemo && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">{selectedMemo.description}</p>
              {selectedMemo.images && selectedMemo.images.length > 0 && (
                <div className="space-y-2">
                  <div className="relative h-[165px]">
                    <img
                      src={selectedMemo.images[currentImageIndex]}
                      alt="Memo detail"
                      className="h-full w-full rounded-md object-cover"
                    />
                    {selectedMemo.images.length > 1 && (
                      <>
                        <div className="absolute inset-x-0 top-1/2 flex -translate-y-1/2 justify-between px-2">
                          <button
                            type="button"
                            onClick={prevImage}
                            className="flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-md"
                          >
                            <ChevronLeft className="h-4 w-4 text-[#354267]" />
                          </button>
                          <button
                            type="button"
                            onClick={nextImage}
                            className="flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-md"
                          >
                            <ChevronRight className="h-4 w-4 text-[#354267]" />
                          </button>
                        </div>
                        <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1.5">
                          {selectedMemo.images.map((image, index) => (
                            <div
                              key={image}
                              className={`h-1.5 rounded-full transition-all ${
                                index === currentImageIndex
                                  ? "w-5 bg-[#354267]"
                                  : "w-1.5 bg-slate-500"
                              }`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              onClick={() => {
                setShowDetailModal(false)
                if (selectedMemo) {
                  handleEditMemo(selectedMemo)
                }
              }}
            >
              <Edit2 className="h-4 w-4" />
              編集
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center text-xl font-bold text-slate-700">
              メモを削除しますか？
            </DialogTitle>
            <DialogDescription className="text-center text-sm text-slate-500">
              削除したメモは復元できません。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-4 sm:flex-col">
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              className="w-full"
              disabled={isDeleting}
            >
              キャンセル
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              className="w-full bg-red-500 hover:bg-red-600 !ml-0"
              loading={isDeleting}
              disabled={isDeleting}
            >
              削除する
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}
